/**
 * Display names — CORE-01B §7.2, §7.3 and the UI spec's "Known corrections to
 * every reference".
 *
 * Rules: THER-041 (the therapist card shows first name plus surname initial),
 * THER-044 (co-assigned therapists see each other's names in the same form).
 *
 * > Display every Client, Therapist, and Rider name as first name plus surname
 * > initial.
 *
 * One rule, one implementation, used on every surface that renders a person:
 * the client's therapist card, the therapist's own client list, the rider's
 * pickup card. The stored `full_name` never reaches a screen.
 *
 * This is NOT the therapist–client data wall (`.claude/rules/data-wall.md`) —
 * that wall keeps client PII away from therapists and hub managers, and it
 * stays exactly as strict. This is the milder, universal display rule that
 * applies in every direction, including the one the wall permits: a therapist's
 * name shown to the client who booked them.
 *
 * `v_booking_therapist_view` still exposes `client_first_name` alone (first
 * name, no initial) and is deliberately left as-is: widening a data-wall view
 * is a migration with a pgTAP negative test, not a display tweak.
 */

/**
 * `"Ana Lopez"` → `"Ana L."`; `"Maria"` → `"Maria"`; `"Ana Maria Lopez"` →
 * `"Ana L."` (first token, last token's initial — a middle name is not a
 * surname).
 *
 * Returns `null` for anything with no usable token so a caller renders its own
 * fallback rather than an empty card.
 */
export function displayName(fullName: string | null | undefined): string | null {
  if (!fullName) return null;
  const tokens = fullName.trim().split(/\s+/).filter(Boolean);
  const first = tokens[0];
  if (!first) return null;
  if (tokens.length === 1) return first;
  const surname = tokens[tokens.length - 1]!;
  // A surname already written as an initial ("Ana L." / "Ana L") must not
  // become "Ana L..".
  const initial = surname[0]!.toUpperCase();
  return `${first} ${initial}.`;
}

/** The avatar fallback when there is no photo — CORE-01B UI §2.1. */
export function displayInitial(fullName: string | null | undefined): string | null {
  const name = displayName(fullName);
  return name ? name[0]!.toUpperCase() : null;
}
